import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNotificationStore } from "../../../Store/Notification";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import BellIconLottie from "../../../Assets/ActionIcons/BellIconLottie.json";
import Lottie from "react-lottie";
import ViewMoreIcon from "../../../Assets/ActionIcons/ViewMoreIcon.svg";
import { formatDistanceToNow } from "../../../Utils/DateUtils";

const Notification = () => {
  const notificationRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const {
    isLoading,
    notifications,
    notificationsCountMap,
    shouldShake,
    getNotifications,
    setNotificationsListOpened,
    notificationsListOpened,
    markReadNotification,
    clearNotification
  } = useNotificationStore();
  const { getAccessTokenSilently } = useAuth0();
  const refreshThings = useCallback(async () => {
    await getNotifications(true, getAccessTokenSilently, 10, "created_at:desc");
  }, [getNotifications, getAccessTokenSilently]);

  const appendNextPage = useCallback(async () => {
    await getNotifications(true, getAccessTokenSilently, 10, "created_at:desc", page + 1);
    setPage(page + 1);
  }, [getNotifications, getAccessTokenSilently, page]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        notificationsListOpened && setNotificationsListOpened(false);
      }
    },
    [notificationsListOpened, setNotificationsListOpened]
  );

  useEffect(() => {
    refreshThings();
    const intervalId = setInterval(refreshThings, 30000);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [refreshThings, handleClickOutside]);

  const totalPages = Math.ceil((notificationsCountMap?.all || 0) / 10);

  const [currentlyClearing, setCurrentlyClearing] = useState<string | null>(null);

  const clearSingleNotification = useCallback(
    async (notificationId: string) => {
      setCurrentlyClearing(notificationId);
      await clearNotification(getAccessTokenSilently, {
        update_type: "some",
        ids: [notificationId]
      });
      setCurrentlyClearing(null);
    },
    [clearNotification, getAccessTokenSilently]
  );

  const markAllAsRead = useCallback(async () => {
    await markReadNotification(getAccessTokenSilently, {
      update_type: "all"
    });
  }, [markReadNotification, getAccessTokenSilently]);

  const clearAll = useCallback(async () => {
    await clearNotification(getAccessTokenSilently, {
      update_type: "all"
    });
    setNotificationsListOpened(false);
  }, [setNotificationsListOpened, clearNotification, getAccessTokenSilently]);

  return (
    <div className="relative mx-4 w-12" ref={notificationRef}>
      <div
        onClick={e => {
          setNotificationsListOpened(!notificationsListOpened);
        }}
      >
        <Lottie
          options={{ animationData: BellIconLottie, autoplay: false }}
          isStopped={!shouldShake}
          isClickToPauseDisabled={true}
          height={52}
        />
      </div>
      {notificationsCountMap && notificationsCountMap?.unread > 0 && (
        <div className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 border-2 border-white rounded-full top-1 end-1">
          {notificationsCountMap.unread}
        </div>
      )}
      {notificationsListOpened && (
        <div className="absolute top-12 right-0 w-96 border border-gray-200 bg-white shadow-2xl">
          <div className="font-semibold p-4 w-full border-b-2 border-gray-200">Notifications</div>
          <div className="overflow-y-auto h-96">
            <>
              {notificationsCountMap?.all === 0 && !isLoading && (
                <div className="p-3 text-center my-12">You have no notifications at this time.</div>
              )}
              {notifications.map(notification => (
                <div
                  id={notification.id}
                  key={notification.id}
                  className={`grid grid-cols-5 group p-3 border-b border-gray-200 flex items-center justify-between transition-all duration-1000 ${
                    !notification.read_at && "bg-[#e6f7ff]"
                  } ${currentlyClearing === notification.id && "opacity-0"}`}
                >
                  <div className="flex flex-col col-span-3">
                    <div>{notification.message.subject}</div>
                    <div className="mt-2">{notification.message.message}</div>
                  </div>
                  <div className="flex flex-col col-span-2 items-end justify-between space-y-3">
                    <button
                      className={`opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                        isLoading && "cursor-progress"
                      }`}
                      disabled={isLoading}
                      onClick={() => clearSingleNotification(notification.id)}
                    >
                      X
                    </button>
                    <div className="text-gray-400 text-xs">
                      {formatDistanceToNow(notification.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              {page < totalPages && (
                <button
                  className={`group flex w-full justify-center p-2 mb-8 items-center ${
                    isLoading ? "opacity-50 cursor-progress" : "cursor-pointer"
                  }`}
                  onClick={appendNextPage}
                  disabled={isLoading}
                >
                  <img
                    src={ViewMoreIcon}
                    alt="View more"
                    className="mr-2 group-hover:animate-pulse"
                  />
                  <div className="text-blue-600 group-hover:text-blue-500">View more</div>
                </button>
              )}
            </>
          </div>
          <div className="px-2 w-full flex justify-between">
            <button
              className={`p-2 text-blue-600 font-semibold hover:text-blue-500 ${
                isLoading && "cursor-progress"
              }`}
              onClick={markAllAsRead}
              disabled={isLoading}
            >
              Mark all as read
            </button>
            <button
              className={`p-2 text-blue-600 font-semibold hover:text-blue-500 ${
                isLoading && "cursor-progress"
              }`}
              disabled={isLoading}
              onClick={clearAll}
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default withAuthenticationRequired(Notification);
